import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Popover } from '../popover';
import {
  Check,
  ClearBtn,
  Dropdown,
  DropdownIcon,
  Field,
  OptionRow,
  Panel,
  Placeholder,
  RemoveBtn,
  SearchContainer,
  SearchInput,
  SelectedText,
  Tag,
  TagText,
} from './styled';
import type { InternalValue, SelectProps } from './types';

function normalize(text: string): string {
  return text.toLowerCase();
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  multi = false,
  searchable = true,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled = false,
  size = 'medium',
  clearable = true,
  maxVisibleTags = 3,
  noOptionsText = 'No options',
  className,
  testId,
  styleProps,
}) => {
  const isControlled = typeof value !== 'undefined';
  const [internalValue, setInternalValue] = React.useState<InternalValue>(() => {
    if (Array.isArray(defaultValue)) return defaultValue;
    if (typeof defaultValue === 'string') return defaultValue ? [defaultValue] : [];
    return [];
  });
  const selectedValues: InternalValue = React.useMemo(() => {
    if (isControlled) {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return value ? [value] : [];
      return [];
    }
    return internalValue;
  }, [isControlled, value, internalValue]);

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState<string>('');
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);

  const fieldRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  const valueSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return options;
    const q = normalize(query);
    return options.filter((opt) => normalize(opt.label).includes(q));
  }, [options, query]);

  const selectedOptions = React.useMemo(
    () => options.filter((o) => valueSet.has(o.value)),
    [options, valueSet]
  );

  const setValue = (next: InternalValue) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(
      multi ? next : (next[0] ?? ''),
      options.filter((o) => next.includes(o.value))
    );
  };

  const toggleValue = (val: string) => {
    if (multi) {
      const next = valueSet.has(val)
        ? selectedValues.filter((v) => v !== val)
        : [...selectedValues, val];
      setValue(next);
    } else {
      const next = [val];
      setValue(next);
      setOpen(false);
    }
    setQuery('');
    setActiveIndex(-1);
  };

  const clearAll = () => {
    setValue([]);
    setQuery('');
    setActiveIndex(-1);
  };

  const moveActive = (delta: number) => {
    if (!filtered.length) return;
    const start = activeIndex < 0 ? 0 : activeIndex;
    let next = start + delta;
    if (next < 0) next = filtered.length - 1;
    if (next >= filtered.length) next = 0;
    setActiveIndex(next);

    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLDivElement>(`[data-index="${next}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      !open &&
      (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')
    ) {
      setOpen(true);
      requestAnimationFrame(() => {

        if (searchable) {
          const inputToFocus = multi ? searchInputRef.current : inputRef.current;
          inputToFocus?.focus();
        }
      });
      e.preventDefault();
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        moveActive(1);
        e.preventDefault();
        break;
      case 'ArrowUp':
        moveActive(-1);
        e.preventDefault();
        break;
      case 'Home':
        setActiveIndex(0);
        e.preventDefault();
        break;
      case 'End':
        setActiveIndex(filtered.length - 1);
        e.preventDefault();
        break;
      case 'Enter':
      case ' ':
        if (open && activeIndex >= 0 && activeIndex < filtered.length) {
          const opt = filtered[activeIndex];
          if (opt && !opt.disabled) toggleValue(opt.value);
          e.preventDefault();
        } else {
          setOpen((o) => !o);
          e.preventDefault();
        }
        break;
      case 'Escape':
        setOpen(false);
        e.preventDefault();
        break;
      case 'Backspace':
        if (multi && !query && selectedValues.length) {
          setValue(selectedValues.slice(0, -1));
          e.preventDefault();
        }
        break;
      default:
        break;
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        moveActive(1);
        e.preventDefault();
        break;
      case 'ArrowUp':
        moveActive(-1);
        e.preventDefault();
        break;
      case 'Home':
        setActiveIndex(0);
        e.preventDefault();
        break;
      case 'End':
        setActiveIndex(filtered.length - 1);
        e.preventDefault();
        break;
      case 'Enter':
      case ' ':
        if (activeIndex >= 0 && activeIndex < filtered.length) {
          const opt = filtered[activeIndex];
          if (opt && !opt.disabled) {
            toggleValue(opt.value);
            e.preventDefault();
          }
        }
        break;
      case 'Escape':
        setOpen(false);
        e.preventDefault();
        break;
      default:

        break;
    }
  };

  const listboxId = React.useId();
  const panelKey = `${listboxId}-${query ? 'q' : 'noq'}`;

  const scrollOptionIntoCenter = React.useCallback((index: number, retryCount = 0) => {
    if (!listRef.current || index < 0) return;
    const container = listRef.current;
    const el = container.querySelector<HTMLDivElement>(`[data-index="${index}"]`);

    if (!el && retryCount < 3) {

      setTimeout(() => scrollOptionIntoCenter(index, retryCount + 1), 10);
      return;
    }

    if (!el) return;

    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offsetTop = elRect.top - containerRect.top + container.scrollTop;
    const target = offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: 'instant' as ScrollBehavior });
  }, []);

  const renderSelectedSummary = () => {
    if (!multi) return null;
    const visible = selectedOptions.slice(0, maxVisibleTags);
    const hiddenCount = selectedOptions.length - visible.length;
    return (
      <>
        {visible.map((opt) => (
          <Tag key={opt.value} aria-label={`Selected ${opt.label}`} $styleProps={styleProps}>
            <TagText>{opt.label}</TagText>
            <RemoveBtn
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                toggleValue(opt.value);
              }}
              aria-label={`Remove ${opt.label}`}
            >
              <XMarkIcon width={16} height={16} />
            </RemoveBtn>
          </Tag>
        ))}
        {hiddenCount > 0 && <Tag $styleProps={styleProps}>+{hiddenCount}</Tag>}
      </>
    );
  };

  const showPlaceholder = selectedValues.length === 0 && !query;

  const getSelectedIndexInFiltered = React.useCallback((): number => {
    if (filtered.length === 0) return -1;
    if (selectedValues.length === 0) return 0;

    const target = multi ? selectedValues[selectedValues.length - 1] : selectedValues[0];
    const idx = filtered.findIndex((o) => o.value === target);
    return idx >= 0 ? idx : 0;
  }, [filtered, selectedValues, multi]);

  React.useEffect(() => {
    if (open) {
      const idx = selectedValues.length > 0 ? getSelectedIndexInFiltered() : 0;
      setActiveIndex(idx);
      requestAnimationFrame(() => {

        if (searchable) {
          const inputToFocus = multi ? searchInputRef.current : inputRef.current;
          inputToFocus?.focus();
        }
        if (selectedValues.length > 0) {
          scrollOptionIntoCenter(idx);
        }
      });
    }
  }, [
    open,
    getSelectedIndexInFiltered,
    multi,
    searchable,
    selectedValues.length,
    scrollOptionIntoCenter,
  ]);

  React.useEffect(() => {
    if (query.trim()) {
      requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.scrollTop = 0;
        }
      });
    }
  }, [query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {

          setQuery('');
          setActiveIndex(-1);
          requestAnimationFrame(() => {
            if (listRef.current) listRef.current.scrollTop = 0;
          });
        }
      }}
      contentPadding='0'
      contentBorderColor={styleProps?.borderColor}
      contentBorderRadius={styleProps?.borderRadius}
      contentZIndex={styleProps?.zIndex ?? 1000}
      placement='bottom-start'
      matchTriggerWidth={true}
      trigger={
        <Field
          ref={fieldRef}
          className={className}
          data-testid={testId}
          role='combobox'
          aria-haspopup='listbox'
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
          onClick={(e) => {
            if (disabled) return;

            e.stopPropagation();
            setOpen(!open);
          }}
          $size={size}
          $styleProps={styleProps}
          $multi={multi}
          $hasSelections={selectedValues.length > 0}
          $open={open}
        >
          {multi && renderSelectedSummary()}
          {!multi && selectedOptions[0] && (
            <SelectedText $styleProps={styleProps}>{selectedOptions[0].label}</SelectedText>
          )}
          {!multi && searchable && !selectedOptions[0] && <Placeholder>{placeholder}</Placeholder>}
          {multi && selectedValues.length === 0 && <Placeholder>{placeholder}</Placeholder>}
          {!searchable && showPlaceholder && <Placeholder>{placeholder}</Placeholder>}
          {clearable && selectedValues.length > 0 && !(open && query && !multi) && (
            <ClearBtn
              type='button'
              aria-label='Clear selection'
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              $multi={multi}
              $hasSelections={selectedValues.length > 0}
            >
              <XMarkIcon width={16} height={16} />
            </ClearBtn>
          )}
          <DropdownIcon $open={open} $disabled={disabled}>
            <ChevronDownIcon width={16} height={16} />
          </DropdownIcon>
        </Field>
      }
    >
      <Panel key={panelKey} $styleProps={styleProps}>
        {searchable && (
          <SearchContainer $size={size}>
            <SearchInput
              ref={multi ? searchInputRef : inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              aria-autocomplete='list'
              aria-controls={listboxId}
              aria-expanded={open}
              $styleProps={styleProps}
              $size={size}
            />
            {clearable && query && (
              <ClearBtn
                type='button'
                aria-label='Clear search'
                onClick={(e) => {
                  e.stopPropagation();
                  setQuery('');
                  setActiveIndex(-1);

                  requestAnimationFrame(() => {
                    const inputToFocus = multi ? searchInputRef.current : inputRef.current;
                    inputToFocus?.focus();
                  });
                }}
                $multi={multi}
                $hasSelections={false}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                }}
              >
                ✕
              </ClearBtn>
            )}
          </SearchContainer>
        )}
        <Dropdown
          ref={listRef}
          id={listboxId}
          role='listbox'
          aria-multiselectable={multi || undefined}
          $styleProps={styleProps}
          $size={size}
        >
          {filtered.length === 0 && (
            <OptionRow
              $disabled
              $active={false}
              $selected={false}
              $styleProps={styleProps}
              $size={size}
              aria-disabled
            >
              {noOptionsText}
            </OptionRow>
          )}
          {filtered.map((opt, actualIndex) => {
            const selected = valueSet.has(opt.value);
            const active = actualIndex === activeIndex;
            const id = `${listboxId}-opt-${actualIndex}`;
            return (
              <OptionRow
                key={opt.value}
                data-index={actualIndex}
                id={id}
                role='option'
                aria-selected={selected}
                $active={active}
                $selected={selected}
                $disabled={!!opt.disabled}
                $styleProps={styleProps}
                $size={size}
                onMouseEnter={() => setActiveIndex(actualIndex)}
                onClick={() => !opt.disabled && toggleValue(opt.value)}
              >
                {opt.label}
                {selected && (
                  <Check>
                    <CheckIcon width={16} height={16} />
                  </Check>
                )}
              </OptionRow>
            );
          })}
        </Dropdown>
      </Panel>
    </Popover>
  );
};